// /contact — the form works; the DETAILS BESIDE IT ARE PLACEHOLDERS.
//
// "Send us a note" opens the same ContactModal the pricing cards use, which
// records a real contact inquiry (POST /contact-inquiry, read in the Super
// Admin console under Contact inquiries — the tenant-admin copy of that list
// was removed on 22 Sep 2026, since the list spans every tenant). That half is
// live, and the form now takes an optional mobile number beside the e-mail.
//
// The email, phone and address are marked "To be confirmed" and the page
// carries a visible placeholder banner, because inventing a support address is
// worse than admitting we have not published one yet. Fill in
// publicPages.contact_{email,phone,address}_value when they exist.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, ArrowRight, LifeBuoy } from 'lucide-react';
import ContactModal from '../../components/subscription/ContactModal';
import PublicPage from './PublicPage';

const DETAILS = [
  { key: 'email', Icon: Mail },
  { key: 'phone', Icon: Phone },
  { key: 'address', Icon: MapPin },
];

export default function Contact() {
  const { t } = useTranslation('publicPages');
  const [open, setOpen] = useState(false);

  return (
    <PublicPage
      testId="contact-page"
      title={t('publicPages.contact_title')}
      lead={t('publicPages.contact_lead')}
      note={t('publicPages.todo_note')}
    >
      <div className="mt-9 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border-2 border-[#4ABDBD]/25 bg-white p-7">
          <h2 className="text-lg font-bold text-gray-900 sm:text-xl">{t('publicPages.contact_form_title')}</h2>
          <p className="mt-2.5 text-[15px] leading-relaxed text-gray-600">{t('publicPages.contact_form_desc')}</p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="contact-page-open"
            className="mt-6 inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] px-6 py-3.5 text-[15px] font-bold text-white shadow-lg shadow-[#4ABDBD]/25 transition-all hover:shadow-xl"
          >
            {t('publicPages.contact_cta')} <ArrowRight size={17} />
          </button>
        </div>

        <div className="rounded-2xl border-2 border-gray-100 bg-white p-7">
          <h2 className="text-lg font-bold text-gray-900 sm:text-xl">{t('publicPages.contact_details_title')}</h2>
          <ul className="mt-5 space-y-4">
            {DETAILS.map((d) => (
              <li key={d.key} className="flex items-start gap-3" data-testid={`contact-detail-${d.key}`}>
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#4ABDBD]/10">
                  <d.Icon size={17} className="text-[#2D8A8A]" />
                </span>
                <span>
                  <span className="block text-[11px] font-bold uppercase tracking-[0.13em] text-gray-400">
                    {t(`publicPages.contact_${d.key}_label`)}
                  </span>
                  {/* TODO(contact-details): real value once published. */}
                  <span className="block text-[15px] font-semibold text-gray-700">
                    {t(`publicPages.contact_${d.key}_value`)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 flex items-start gap-4 rounded-2xl border-2 border-gray-100 bg-[#f0faf9] p-7">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B]">
          <LifeBuoy size={20} className="text-white" />
        </span>
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t('publicPages.contact_support_title')}</h2>
          <p className="mt-2 text-[15px] leading-relaxed text-gray-600">{t('publicPages.contact_support_desc')}</p>
        </div>
      </div>

      {open && <ContactModal source="landing" onClose={() => setOpen(false)} />}
    </PublicPage>
  );
}
